import { Link, useNavigate, useParams } from "react-router-dom";
import "./UserDetailPage.scss";
import { useUserDetailViewModel } from "../viewmodel/useUserDetailViewModel";
import Loader from "@/core/components/loader/Loader";
import { SvgIcon } from "@/core/components/svgIcon/SvgIcon";
import { useState, useEffect } from "react";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import CallCard from "@/features/calls/components/CallCard/CallCard";
import { adaptDeclaredInterestCallToCall } from "@/features/partners/utils/callAdapter";
import { Share } from "@/core/components/share/Share";
import { IdeaListItem } from "@/features/ideas/components/IdeaListItem";
import { UserIdeaGridItem } from "@/features/ideas/components/UserIdeaGridItem";
import { SEO } from "@/core/components/SEO/SEO";
import { useStartConversation } from "@/features/chat/viewmodel/useChatViewModel";

export const UserDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const [currentCallIndex, setCurrentCallIndex] = useState(0);
  const [currentIdeaIndex, setCurrentIdeaIndex] = useState(0);
  const [showAllSectors, setShowAllSectors] = useState(false);
  const { user, loading, error, toggleUserFollow } = useUserDetailViewModel(id);
  const navigate = useNavigate();
  const { startConversation, loading: startingConversation } =
    useStartConversation();
  const handleUserFollow = (userId: string, isFollowing: boolean) => {
    toggleUserFollow(userId, isFollowing);
  };

  // Carousel navigation for declared interest calls
  const callsPerSlide = 3;
  const declaredCalls = user?.declared_interest_calls.data || [];
  const totalCallSlides = Math.ceil(declaredCalls.length / callsPerSlide);

  const handlePreviousCall = () => {
    setCurrentCallIndex((prev) => (prev > 0 ? prev - 1 : totalCallSlides - 1));
  };

  const handleNextCall = () => {
    setCurrentCallIndex((prev) => (prev < totalCallSlides - 1 ? prev + 1 : 0));
  };

  // Carousel navigation for ideas - responsive items per slide
  const getIdeasPerSlide = () => {
    if (window.innerWidth > 1024) return 3;
    if (window.innerWidth > 768) return 2;
    return 1;
  };

  const [ideasPerSlide, setIdeasPerSlide] = useState(getIdeasPerSlide);
  const userIdeas = user?.ideas?.data || [];
  const totalIdeaSlides = Math.ceil(userIdeas.length / ideasPerSlide);

  useEffect(() => {
    const handleResize = () => {
      const newIdeasPerSlide = getIdeasPerSlide();
      setIdeasPerSlide(newIdeasPerSlide);
      // Reset index if current slide would be out of bounds
      const newTotalSlides = Math.ceil(userIdeas.length / newIdeasPerSlide);
      if (currentIdeaIndex >= newTotalSlides) {
        setCurrentIdeaIndex(Math.max(0, newTotalSlides - 1));
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [userIdeas.length, currentIdeaIndex]);

  const handlePreviousIdea = () => {
    setCurrentIdeaIndex((prev) => (prev > 0 ? prev - 1 : totalIdeaSlides - 1));
  };

  const handleNextIdea = () => {
    setCurrentIdeaIndex((prev) => (prev < totalIdeaSlides - 1 ? prev + 1 : 0));
  };

  if (loading) {
    return (
      <div className="user-detail-page">
        <div className="pageTitle">User Details</div>
        <Loader overlay />
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-detail-page">
        <div className="pageTitle">User Details</div>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="user-detail-page">
        <div className="pageTitle">User Details</div>
        <div className="error-message">User not found</div>
      </div>
    );
  }

  const fullName =
    `${user.firstname || ""} ${user.lastname || ""}`.trim() || user.username;

  return (
    <>
      <div className="container user-detail-header">
        <SEO
          title={`${user ? `${user.firstname} ${user.lastname}` : "User"} | EUcalls`}
          description={`${user ? `${user.firstname} ${user.lastname}` : "User"}`}
        />
        <div className="back-button" onClick={() => navigate(-1)}>
          <SvgIcon icon="icon-arrow-left" />
          <span>Back</span>
        </div>
        {user.is_me && (
          <Link to={"/settings"} className="editProfile">
            <SvgIcon icon="icon-editProfile" /> Edit profile
          </Link>
        )}
      </div>
      <div className="user-detail-page">
        <div className="container"></div>
        <div className="user-container container">
          <div className="user-header desktop">
            <div className="header-left">
              <div className="user-logo">
                {user.user_picture_uri &&
                user.user_picture_uri !== "pictures/logo_not_available.jpg" ? (
                  <img src={`${user.user_picture_uri}`} alt={fullName} />
                ) : (
                  <span>{fullName?.charAt(0)?.toUpperCase() || "U"}</span>
                )}
              </div>
              <div className="user-info">
                <h1>{fullName}</h1>
                <div className="user-meta" style={{ gap: "0px" }}>
                  <span className="meta-item">
                    {user.job_title}
                    {user.job_title && user.partner
                      ? " "
                      : !user.job_title && user.partner
                        ? "works "
                        : ""}
                    {user.partner && (
                      <a href={`/partner/${user.partner.id}`}>
                        at
                        <strong> {user.partner.name}</strong>
                      </a>
                    )}
                  </span>
                </div>
                <div className="user-meta" style={{ paddingTop: ".3rem" }}>
                  <span className="meta-item">
                    <SvgIcon icon="icon-followers" />
                    {user.followers_count} Followers
                  </span>
                  <span className="meta-item">
                    <SvgIcon icon="icon-following" />
                    {user.following_count} Following
                  </span>
                </div>
              </div>
            </div>
            <div className="header-right">
              <div className="header-right">
                {user.linkedin !== "" && (
                  <a
                    href={`https://www.linkedin.com/in/${user.linkedin.replace("https://www.linkedin.com/in/", "")}`}
                    target="_blank"
                    className={`social-btn ${user.linkedin === "" ? "noUrl" : ""}`}
                  >
                    <SvgIcon icon="icon-linkedIn" />
                  </a>
                )}
                <Share />
              </div>
              {!user.is_me && (
                <>
                  <button
                    className={`follow-btn ${user.is_followed_by_me ? "following" : ""}`}
                    onClick={() =>
                      handleUserFollow(user.id, user.is_followed_by_me)
                    }
                  >
                    <SvgIcon
                      icon={
                        user.is_followed_by_me
                          ? "icon-user-minus"
                          : "icon-user-plus"
                      }
                    />
                    {user.is_followed_by_me ? "Unfollow" : "Follow"}
                  </button>
                  <button
                    className="message-btn"
                    onClick={() => startConversation(user.id)}
                    disabled={startingConversation}
                    title="Send message"
                  >
                    <SvgIcon icon="icon-message" />
                  </button>
                </>
              )}
            </div>
          </div>
          <div className="user-header mobile">
            <div className="header-left">
              <div className="user-logo">
                {user.user_picture_uri &&
                user.user_picture_uri !== "pictures/logo_not_available.jpg" ? (
                  <img src={`${user.user_picture_uri}`} alt={fullName} />
                ) : (
                  <span>{fullName?.charAt(0)?.toUpperCase() || "U"}</span>
                )}
              </div>
              <div className="user-info">
                <h1>{fullName}</h1>
              </div>
            </div>
          </div>

          <div className="user-header mobile">
            <div className="header-left">
              <div className="user-info">
                <div className="user-meta">
                  {user.job_title && (
                    <span className="meta-item">
                      {user.job_title}{" "}
                      {user.partner && (
                        <a href={`/partner/${user.partner.id}`}>
                          <strong>at {user.partner.name}</strong>
                        </a>
                      )}
                    </span>
                  )}
                </div>
                <div className="user-meta" style={{ paddingTop: ".3rem" }}>
                  <span className="meta-item">
                    <SvgIcon icon="icon-followers" />
                    {user.followers_count} Followers
                  </span>
                  <span className="meta-item">
                    <SvgIcon icon="icon-following" />
                    {user.following_count} Following
                  </span>
                </div>
              </div>
            </div>
            <div className="header-right">
              <div className="header-right">
                {user.linkedin !== "" && (
                  <a
                    href={`https://www.linkedin.com/in/${user.linkedin.replace("https://www.linkedin.com/in/", "")}`}
                    target="_blank"
                    className={`social-btn ${user.linkedin === "" ? "noUrl" : ""}`}
                  >
                    <SvgIcon icon="icon-linkedIn" />
                  </a>
                )}
                <Share />
              </div>
              {!user.is_me && (
                <>
                  <button
                    className={`follow-btn ${user.is_followed_by_me ? "following" : ""}`}
                    onClick={() =>
                      handleUserFollow(user.id, user.is_followed_by_me)
                    }
                  >
                    <SvgIcon
                      icon={
                        user.is_followed_by_me
                          ? "icon-user-minus"
                          : "icon-user-plus"
                      }
                    />
                    {user.is_followed_by_me ? "Unfollow" : "Follow"}
                  </button>
                  <button
                    className="message-btn"
                    onClick={() => startConversation(user.id)}
                    disabled={startingConversation}
                    title="Send message"
                  >
                    <SvgIcon icon="icon-message" />
                  </button>
                </>
              )}
            </div>
          </div>

          {user.sectors && user.sectors.length > 0 && (
            <div className="user-tags">
              {user.sectors
                .slice(0, showAllSectors ? user.sectors.length : 6)
                .map((sector, index) => (
                  <span key={index} className="tag">
                    {sector.name}
                  </span>
                ))}
              {user.sectors.length > 6 && (
                <span
                  className="tag more"
                  onClick={() => setShowAllSectors(!showAllSectors)}
                  style={{ cursor: "pointer" }}
                >
                  {showAllSectors ? "Show less" : `+${user.sectors.length - 6}`}
                </span>
              )}
            </div>
          )}
          <div className="user-body">
            <div className="content-left">
              <section className="description-section">
                <h2>About</h2>

                {user.description && (
                  <div
                    className="description-content"
                    dangerouslySetInnerHTML={{ __html: user.description }}
                  />
                )}
              </section>
            </div>

            <div className="content-right">
              {user.expertise && user.expertise.length > 0 && (
                <section className="expertise-section">
                  <h2>Expertise</h2>
                  <div className="expertise-tags">
                    {user.expertise.map((exp) => (
                      <span key={exp.id} className="expertise-tag">
                        <div className="expertise-tag-icon">
                          <SvgIcon icon="icon-checkmark" />
                        </div>
                        {exp.name}
                      </span>
                    ))}
                  </div>
                </section>
              )}

              {user.documents && user.documents.length > 0 && (
                <section className="documents-section">
                  <h2>Documents</h2>
                  <div className="document-list">
                    {user.documents.map((doc, index) => (
                      <div key={index} className="document-item">
                        <div className="doc-icon pdf">
                          <span>PDF</span>
                        </div>
                        <div className="doc-info">
                          <span className="doc-name">{doc.filename}</span>
                          {doc.description && (
                            <span className="doc-description">
                              {doc.description}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </div>
        </div>
        {/* Declared Interest Calls Section */}
        {user.declared_interest_calls &&
          user.declared_interest_calls.data &&
          user.declared_interest_calls.data.length > 0 && (
            <section className="declared-calls-section">
              <div className="declared-calls-header">
                <h2>
                  Declared interest in calls (
                  {user.declared_interest_calls.data.length})
                </h2>
                <div className="navigation-arrows">
                  <Button
                    icon="pi pi-chevron-left"
                    text
                    className="nav-arrow"
                    onClick={handlePreviousCall}
                    disabled={totalCallSlides <= 1}
                  />
                  <Button
                    icon="pi pi-chevron-right"
                    text
                    className="nav-arrow"
                    onClick={handleNextCall}
                    disabled={totalCallSlides <= 1}
                  />
                </div>
              </div>

              <div className="declared-calls-carousel">
                <div
                  className="declared-calls-track"
                  style={{
                    transform: `translateX(-${currentCallIndex * 100}%)`,
                  }}
                >
                  {declaredCalls.map((call) => (
                    <div key={call.id} className="declared-call-item">
                      <CallCard
                        call={adaptDeclaredInterestCallToCall(call)}
                        showPartners
                      />
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}
        {/* User Ideas Section */}
        {user.ideas && user.ideas.data && user.ideas.data.length > 0 && (
          <section className="user-ideas-section">
            <div className="user-ideas-header">
              <h2>Ideas ({user.ideas.count})</h2>
              <div className="navigation-arrows">
                <Button
                  icon="pi pi-chevron-left"
                  text
                  className="nav-arrow"
                  onClick={handlePreviousIdea}
                  disabled={totalIdeaSlides <= 1}
                />
                <Button
                  icon="pi pi-chevron-right"
                  text
                  className="nav-arrow"
                  onClick={handleNextIdea}
                  disabled={totalIdeaSlides <= 1}
                />
              </div>
            </div>

            <div className="user-ideas-carousel">
              <div
                className="user-ideas-track"
                style={{ transform: `translateX(-${currentIdeaIndex * 100}%)` }}
              >
                {userIdeas.map((idea) => (
                  <div key={idea.id} className="user-idea-item">
                    <UserIdeaGridItem idea={idea} />
                  </div>

                  // <div key={idea.id} className="user-idea-item">
                  //   <div className="idea-card eu-card">
                  //     <div className="idea-header">

                  //       <h3 className="idea-title">{idea.title}</h3>
                  //     </div>

                  //     <p className="idea-description">{idea.description}</p>

                  //     {idea.call && (
                  //       <div className="related-call">
                  //         <span className="call-label">Related Call:</span>
                  //         <span className="call-title">{idea.call.title}</span>
                  //       </div>
                  //     )}

                  //     {idea.looking_for && idea.looking_for.length > 0 && (
                  //       <div className="looking-for-section">
                  //         <span className="section-label">Looking for:</span>
                  //         <div className="looking-for-tags">
                  //           {idea.looking_for.map((item) => (
                  //             <Tag key={item.id} value={item.name} className="looking-for-tag" />
                  //           ))}
                  //         </div>
                  //       </div>
                  //     )}

                  //     {idea.partnership && idea.partnership.length > 0 && (
                  //       <div className="partnership-section">
                  //         <span className="section-label">Partnership:</span>
                  //         <div className="partnership-logos">
                  //           {idea.partnership.slice(0, 3).map((partner) => (
                  //             <div key={partner.id} className="partner-logo-wrapper">
                  //               {partner.logo_uri && partner.logo_uri !== 'partner-images/logo_not_available.jpg' ? (
                  //                 <img src={`/${partner.logo_uri}`} alt="Partner" className="partner-logo" />
                  //               ) : (
                  //                 <div className="partner-logo placeholder">
                  //                   <span>P</span>
                  //                 </div>
                  //               )}
                  //             </div>
                  //           ))}
                  //           {idea.partnership.length > 3 && (
                  //             <span className="more-partners">+{idea.partnership.length - 3}</span>
                  //           )}
                  //         </div>
                  //       </div>
                  //     )}
                  //   </div>
                  // </div>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
};
